/** Hora para UI y persistencia: HH:mm (sin segundos). */
import { getLocalizedScheduleType } from "../i18n/scheduleLabels";

export function formatClockTime(time) {
  if (time == null || time === "") return "";
  const str = String(time).trim();
  const iso = str.match(/T(\d{1,2}):(\d{2})/);
  if (iso) {
    return `${iso[1].padStart(2, "0")}:${iso[2]}`;
  }
  const match = str.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return str;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

export function formatClockTimeRange(start, end) {
  const s = formatClockTime(start);
  const e = formatClockTime(end);
  if (!s && !e) return "";
  if (!e) return s;
  if (!s) return e;
  return `${s} - ${e}`;
}

export function normalizeAppointmentTimes(appointment) {
  if (!appointment) return appointment;
  return {
    ...appointment,
    start_time: formatClockTime(appointment.start_time),
    end_time: formatClockTime(appointment.end_time),
  };
}

export function normalizeScheduleTimes(schedule) {
  if (!schedule) return schedule;
  return {
    ...schedule,
    start_time: formatClockTime(schedule.start_time),
    end_time: formatClockTime(schedule.end_time),
  };
}

/** Turno activo que ocupa un horario (misma lógica que generateTimeSlots). */
export function isOccupiedAppointment(apt) {
  if (!apt) return false;
  return apt.status !== "cancelled" && apt.status !== "available";
}

export function generateTimeSlots(
  startTime,
  endTime,
  slotDuration,
  appointmentType,
  existingAppointments = [],
  consultationMode = CONSULTATION_MODE.IN_PERSON
) {
  const slots = [];
  const start = parseTime(startTime);
  const end = parseTime(endTime);

  let current = start;

  while (current < end) {
    const slotStart = formatTime(current);
    const slotEnd = formatTime(current + slotDuration);

    // Check if there's an existing appointment for this slot (any type blocks the slot)
    // Normalize time to HH:MM for comparison (DB may store HH:MM:SS)
    const existingAppointment = existingAppointments.find(
      (apt) =>
        formatClockTime(apt.start_time) === slotStart &&
        apt.status !== "cancelled" &&
        apt.status !== "available"
    );

    slots.push({
      startTime: slotStart,
      endTime: slotEnd,
      isAvailable: !existingAppointment,
      appointmentType,
      consultationMode: consultationMode || CONSULTATION_MODE.IN_PERSON,
      appointment: existingAppointment,
    });

    current += slotDuration;
  }

  return slots;
}

export function parseTime(timeString) {
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
}

export function formatTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

export function getDayOfWeek(date) {
  return date.getDay();
}

export function formatDate(date) {
  // Handle different date formats that might come from Supabase.
  // Use local date components (not UTC) so calendar dates are correct in any timezone.
  let dateObj;

  if (!date) {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  if (typeof date === "string") {
    // Fechas solo-día de Supabase (YYYY-MM-DD): evitar desfase por UTC al parsear
    const dateOnly = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnly) {
      return dateOnly[1];
    }
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) {
    console.error("Invalid date:", date);
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Fecha para UI (es-AR): día/mes/año, ej. 24/5/2026. Sin desfase UTC en YYYY-MM-DD. */
export function formatDateDMY(date) {
  if (date == null || date === "") return "";

  if (typeof date === "string") {
    const dateOnly = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      return `${Number(d)}/${Number(m)}/${y}`;
    }
  }

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string" && date.includes("T")) {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date);
  }

  if (Number.isNaN(dateObj.getTime())) return "";
  return `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
}

/** Fecha y hora para UI: día/mes/año HH:mm */
export function formatDateTimeDMY(value) {
  if (value == null || value === "") return "";
  const str = String(value).trim();
  const dateOnly = str.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) {
    const datePart = formatDateDMY(dateOnly[1]);
    const timeMatch = str.match(/T(\d{2}):(\d{2})/);
    return timeMatch ? `${datePart} ${timeMatch[1]}:${timeMatch[2]}` : datePart;
  }
  return formatDateDMY(new Date(str));
}

export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Turno en el mismo día: un horario de hoy que ya empezó no se puede asignar
 * (se ofrece desde el siguiente slot a la hora actual). Días pasados → true; días futuros → false.
 */
export function isSlotInPast(date, startTime, now = new Date()) {
  const dateStr = formatDate(date);
  const todayStr = formatDate(now);
  if (dateStr !== todayStr) return dateStr < todayStr;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return parseTime(formatClockTime(startTime)) < nowMinutes;
}

/** @deprecated Usar getLocalizedDayName() desde i18n/scheduleLabels */
export const DAYS_OF_WEEK = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

/** Formato de consulta: el horario puede ser presencial, virtual o ambas; el turno, presencial o virtual. */
export const CONSULTATION_MODE = {
  IN_PERSON: "in_person",
  VIRTUAL: "virtual",
  BOTH: "both",
};

/** Modalidad del horario (default presencial para horarios previos a la funcionalidad). */
export function getScheduleConsultationMode(schedule) {
  return schedule?.consultation_mode || CONSULTATION_MODE.IN_PERSON;
}

/**
 * Modalidad a guardar en el turno según el slot elegido: si el horario es fijo se usa esa,
 * si es "ambas" se usa la elegida (null si todavía no eligió).
 */
export function resolveAppointmentConsultationMode(slotMode, chosenMode) {
  if (slotMode === CONSULTATION_MODE.BOTH) return chosenMode || null;
  return slotMode || CONSULTATION_MODE.IN_PERSON;
}

export const SCHEDULE_TYPE = {
  HABITUAL: "habitual",
  OCCASIONAL: "occasional",
  BLOCKED: "blocked",
};

export const SCHEDULE_TYPE_LABELS = {
  habitual: "Habitual",
  occasional: "Ocasional",
  blocked: "Bloqueado",
};

export function getScheduleType(schedule) {
  return schedule?.schedule_type || SCHEDULE_TYPE.HABITUAL;
}

export function getScheduleTypeLabel(schedule) {
  return getLocalizedScheduleType(getScheduleType(schedule));
}

/** Fechas específicas (ocasional / bloqueado) como YYYY-MM-DD */
export function getScheduleSpecificDates(schedule) {
  const raw = schedule?.specific_dates;
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((d) => {
    if (typeof d === "string") {
      const m = d.match(/^(\d{4}-\d{2}-\d{2})/);
      return m ? m[1] : formatDate(d);
    }
    return formatDate(d);
  });
}

export function formatScheduleSpecificDates(schedule) {
  const dates = getScheduleSpecificDates(schedule);
  if (dates.length === 0) return "";
  return dates.map(formatDateDMY).join(", ");
}

export function isDateBlockedForDoctor(schedules, doctorId, dateStr) {
  return schedules.some(
    (s) =>
      String(s.doctor_id) === String(doctorId) &&
      s.is_active &&
      getScheduleType(s) === SCHEDULE_TYPE.BLOCKED &&
      getScheduleSpecificDates(s).includes(dateStr)
  );
}

/** Horarios activos que generan turnos para una fecha (excluye bloqueados). */
export function getActiveSchedulesForDate(schedules, doctorId, date) {
  const dateStr = formatDate(date);
  const dayOfWeek = date.getDay();

  if (isDateBlockedForDoctor(schedules, doctorId, dateStr)) {
    return [];
  }

  return schedules.filter((s) => {
    if (String(s.doctor_id) !== String(doctorId) || !s.is_active) return false;
    const type = getScheduleType(s);
    if (type === SCHEDULE_TYPE.BLOCKED) return false;
    if (type === SCHEDULE_TYPE.OCCASIONAL) {
      return getScheduleSpecificDates(s).includes(dateStr);
    }
    return Number(s.day_of_week) === dayOfWeek;
  });
}
